import { Form, Formik } from 'formik';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import CustomTextArea from '../../../components/text-area/textArea';
import { useGetFeaturesPermissionsDistributable, usePostFeatureSet } from '../../../hooks';
import { type ModalStatusType } from '../../../types/common';
import { FEATURE_SET_VALIDATION } from '../../../utils/validations/validations';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { SORTED_FIELD_PERMISSION, SORTED_MODULE_PERMISSION } from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import { convertDualPermissions } from '../../../utils/permission/permissionAdapter';
import { getFilteredPermissionData } from '../../../utils';
import {
  PermissionMatrixForm,
  useFilteredPermissionMatrix,
  PERMISSION_MODAL_TOOLTIP,
  getPermissionModalTitle,
  type PermissionChangeLog,
} from '../shared/permission-matrix';

interface FeatureSetModalTypes {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  id?: string;
}

const FeatureSetModal = ({
  isModalOpen,
  closeModal,
  initialData,
  setModalStatus,
  modalStatus,
  handleRequest,
  id,
}: FeatureSetModalTypes): JSX.Element => {
  const { mutate, isLoading } = usePostFeatureSet(handleRequest, closeModal, initialData.id);
  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;
  const [manualSearch, setManualSearch] = useState('');

  const permissionChangeLogs = initialData.data?.permissionChangeLogs as
    | PermissionChangeLog[]
    | null
    | undefined;

  const { data: distributablePermissions } = useGetFeaturesPermissionsDistributable(handleRequest);

  const [moduleLevelPermissions, setModuleLevelPermissions] = useState<any[]>([]);
  const [fieldLevelPermissionsList, setFieldLevelPermissionsList] = useState<any[]>([]);

  useEffect(() => {
    if (distributablePermissions?.fieldLevelPermissions) {
      setModuleLevelPermissions(distributablePermissions?.permissions ?? []);
      setFieldLevelPermissionsList(distributablePermissions?.fieldLevelPermissions ?? []);
    }
  }, [distributablePermissions]);

  const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
    convertDualPermissions(moduleLevelPermissions),
    'CustomerService.Customer.',
    allFieldValues
  );
  const { fieldLevelPermissionsGroups } = getFilteredPermissionData(
    convertDualPermissions(fieldLevelPermissionsList),
    'CustomerService.Customer.',
    allFieldValues
  );

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  const { filteredData, filteredFieldLevelData } = useFilteredPermissionMatrix({
    filterNonFieldLevelPermission,
    filterFieldLevelPermission,
    manualSearch,
    allFieldValues,
  });

  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  const handleSubmit = (values: any): void => {
    const permissions = Object.keys(values?.permissions)
      .filter((x) => values.permissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));
    const fieldLevelPermissions = Object.keys(values?.fieldLevelPermissions)
      .filter((x) => values.fieldLevelPermissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));

    const payload = {
      ...values,
      permissions: permissions.concat(fieldLevelPermissions),
    };
    mutate(payload);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={FEATURE_SET_VALIDATION}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, handleChange, errors, values, setFieldValue }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            size={modalStatus !== 'Add' ? '3xl' : 'lg'}
            title={getPermissionModalTitle(modalStatus, 'featureSet', initialData.data.name)}
            valueEdited={_.isEqual(values, initialData.data)}
            id={id}
            showToolTip={true}
            tooltipText={PERMISSION_MODAL_TOOLTIP}
            children={
              <Form id='featureSetForm'>
                <ModalBody id='featureSetModalBody'>
                  <div className='grid sm:grid-cols-4 form-gap'>
                    <div className='sm:col-span-3'>
                      <InputText
                        type='text'
                        label='Name'
                        name='name'
                        id='name'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                    </div>
                    <div className='sm:col-span-3'>
                      <CustomTextArea
                        label='Description'
                        rows='5'
                        name='description'
                        id='description'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                      />
                    </div>
                  </div>

                  <PermissionMatrixForm
                    variant='single'
                    modalStatus={modalStatus}
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    allFieldValues={allFieldValues}
                    filteredData={filteredData}
                    filteredFieldLevelData={filteredFieldLevelData}
                    nonFieldLevelPermissionsGroups={nonFieldLevelPermissionsGroups}
                    fieldLevelPermissionsGroups={fieldLevelPermissionsGroups}
                    filterNonFieldLevelPermission={filterNonFieldLevelPermission}
                    filterFieldLevelPermission={filterFieldLevelPermission}
                    manualSearch={manualSearch}
                    onSearchChange={setManualSearch}
                    searchInputId='searchFeatureSetInput'
                    handleCheckedAll={handleCheckedAll}
                    permissionChangeLogs={permissionChangeLogs}
                  />
                </ModalBody>
                <ModalFooterComponent
                  id='featureSetModalFooter'
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  isEditShow
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  urlName='feature-set'
                  valueChanged={_.isEqual(values, initialData.data)}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default FeatureSetModal;
